import { useState, useMemo, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { 
  TrendingUp, Activity, DollarSign, Bot, AlertTriangle,
  MoreHorizontal, Eye, Pause, Play, Square,
  Zap, Target, BarChart3, Clock, ShieldAlert, ChevronDown, Shield, Wallet,
  Rocket, ArrowUpRight, ArrowDownRight, FileText, Brain, Gauge, Bell,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useActiveStrategies, ActiveStrategy } from '@/hooks/useActiveStrategies';
import { useDemoAccountSummary } from '@/hooks/useDemoAccountSummary';
import { useMarketPrices } from '@/hooks/useMarketPrices';
import { EmptyState } from '@/components/ui/empty-state';
import { formatQty, formatPrice, formatPnl, formatTimeAgo } from '@/utils/formatters';
import { useQuery, useIsFetching, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { usePortfolioHealth } from '@/hooks/usePortfolioHealth';
import { useHealthAlerts } from '@/hooks/useHealthAlerts';
import { useMarketRegimes } from '@/hooks/useMarketRegimes';
import { useAISignals } from '@/hooks/useAISignals';
import { useNotifications } from '@/hooks/useNotifications';
import { HealthScoreGauge } from '@/components/portfolio/HealthScoreGauge';
import NotificationCenter from '@/components/dashboard/NotificationCenter';
import { AllocationPieChart } from '@/components/portfolio/AllocationPieChart';
import { CorrelationHeatmap } from '@/components/portfolio/CorrelationHeatmap';
import { LineChart, Line, ResponsiveContainer } from 'recharts';
import OnboardingChecklist from '@/components/dashboard/OnboardingChecklist';
import { useOnboardingSetup } from '@/hooks/useOnboardingSetup';
import { toast } from '@/hooks/use-toast';

// formatPnl imported from @/utils/formatters

const getStrategyIcon = (name: string) => {
  const lower = name.toLowerCase();
  if (lower.includes('momentum') || lower.includes('scalp')) return Zap;
  if (lower.includes('breakout') || lower.includes('hunter')) return Target;
  if (lower.includes('trend')) return TrendingUp;
  if (lower.includes('range') || lower.includes('grid')) return BarChart3;
  return Activity;
};

/* ─── Terminal Card wrapper ─── */
const TCard = ({ children, className = '', accent }: { children: React.ReactNode; className?: string; accent?: 'profit' | 'loss' | 'purple' }) => (
  <div className={`rounded-xl bg-[hsl(var(--background-secondary))] border border-[hsl(var(--card-border))] ${
    accent === 'profit' ? 'border-l-[3px] border-l-[hsl(var(--accent-green))]' :
    accent === 'loss' ? 'border-l-[3px] border-l-[hsl(var(--accent-red))]' :
    accent === 'purple' ? 'border-l-[3px] border-l-[hsl(var(--accent-purple))]' : ''
  } ${className}`}>
    {children}
  </div>
);


interface DashboardHomeProps {
  onPageChange?: (page: string) => void;
}

const DashboardHome = ({ onPageChange }: DashboardHomeProps = {}) => {
  const navigate = useNavigate();
  const { user, updateUserMetadata } = useAuth();
  const { strategies, loading: strategiesLoading, totalPnl, avgWinRate, activeCount, totalTrades } = useActiveStrategies();
  const { totalBalance, totalInitialBalance, activeAccounts, openPositions, todayTrades, loading: summaryLoading } = useDemoAccountSummary();
  const { prices: marketPrices, isLoading: marketLoading } = useMarketPrices(30000);
  const { health, snapshots, isLoading: healthLoading } = usePortfolioHealth();
  const { regimes } = useMarketRegimes();
  const { pendingSignals, recentSignals, isLoading: signalsLoading } = useAISignals();
  const queryClient = useQueryClient();

  // Bulk action state for strategy panel dropdown
  const [showKillDialog, setShowKillDialog] = useState(false);
  const [killing, setKilling] = useState(false);
  const [pausing, setPausing] = useState(false);
  const [closingAll, setClosingAll] = useState(false);

  const handlePauseAll = async () => {
    if (!user?.id || pausing) return;
    setPausing(true);
    try {
      const { data, error } = await supabase
        .from('strategy_instances')
        .update({ status: 'paused' })
        .eq('user_id', user.id)
        .eq('status', 'active')
        .select('id');
      if (error) throw error;
      toast({ title: 'Strategies paused', description: `${data?.length ?? 0} active strategies paused.` });
      queryClient.invalidateQueries({ queryKey: ['strategy-instances-map', user.id] });
      queryClient.invalidateQueries({ queryKey: ['active-strategies'] });
    } catch (e: any) {
      toast({ title: 'Pause failed', description: e.message || 'Unknown error', variant: 'destructive' });
    } finally {
      setPausing(false);
    }
  };

  const handleCloseAllPositions = async () => {
    if (!user?.id || closingAll) return;
    const positions = openPositionsData ?? [];
    if (positions.length === 0) {
      toast({ title: 'No open positions to close' });
      return;
    }
    setClosingAll(true);
    let ok = 0, failed = 0;
    try {
      for (const pos of positions) {
        try {
          const closeSide = pos.side === 'long' ? 'sell' : 'buy';
          const isLive = instanceMap[pos.strategy_instance_id]?.execution_mode === 'live';
          const fnName = isLive ? 'broker-execute' : 'place-manual-order';
          const { error: orderErr } = await supabase.functions.invoke(fnName, {
            body: { symbol: pos.symbol, side: closeSide, quantity: pos.quantity, order_type: 'market' },
          });
          if (orderErr) throw orderErr;
          const { error: updErr } = await supabase
            .from('strategy_positions')
            .update({ status: 'closed', closed_at: new Date().toISOString() })
            .eq('id', pos.id);
          if (updErr) throw updErr;
          ok++;
        } catch {
          failed++;
        }
      }
      toast({
        title: failed === 0 ? 'All positions closed' : 'Close completed with errors',
        description: `${ok} closed${failed ? `, ${failed} failed` : ''}.`,
        variant: failed === 0 ? 'default' : 'destructive',
      });
      queryClient.invalidateQueries({ queryKey: ['dashboard-positions', user.id] });
    } finally {
      setClosingAll(false);
    }
  };

  const handleEmergencyKill = async () => {
    if (killing) return;
    setKilling(true);
    try {
      const { data, error } = await supabase.functions.invoke('emergency-stop');
      if (error) throw error;
      toast({
        title: 'Emergency stop activated',
        description: data?.message || 'All trading halted.',
      });
      setShowKillDialog(false);
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: ['dashboard-positions', user.id] });
        queryClient.invalidateQueries({ queryKey: ['strategy-instances-map', user.id] });
        queryClient.invalidateQueries({ queryKey: ['active-strategies'] });
      }
    } catch (e: any) {
      toast({ title: 'Emergency stop failed', description: e.message || 'Unknown error', variant: 'destructive' });
    } finally {
      setKilling(false);
    }
  };

  useOnboardingSetup();
  useHealthAlerts(health);

  // First signal celebration
  useEffect(() => {
    if (!user) return;
    const completedSteps: string[] = user.user_metadata?.onboarding_completed_steps || [];
    if (completedSteps.includes('signal_reviewed') || !user.user_metadata?.onboarding_auto_setup) return;

    const channel = supabase
      .channel('onboarding-signals')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'ai_signals' }, async () => {
        toast({ title: '🎉 Your first AI signal is ready!', description: 'Review it now to complete your onboarding.' });
        await updateUserMetadata({ onboarding_completed_steps: [...completedSteps, 'signal_reviewed'] });
        window.dispatchEvent(new CustomEvent('newSignalAlert'));
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user]);

  // AI engine countdown timer
  const [countdown, setCountdown] = useState(900);
  useEffect(() => {
    const interval = setInterval(() => {
      setCountdown(prev => prev <= 0 ? 900 : prev - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  const countdownMin = Math.floor(countdown / 60);
  const countdownSec = countdown % 60;

  // Regime breakdown
  const regimeCounts = useMemo(() => {
    const counts = { trending: 0, ranging: 0, volatile: 0, crisis: 0 };
    if (regimes) {
      for (const r of regimes) {
        if (r.regime in counts) counts[r.regime as keyof typeof counts]++;
      }
    }
    return counts;
  }, [regimes]);

  const dominantRegime = useMemo(() => {
    if (!regimes || regimes.length === 0) return null;
    let max = 0, dominant = 'ranging';
    for (const [regime, count] of Object.entries(regimeCounts)) {
      if (count > max) { max = count; dominant = regime; }
    }
    return { regime: dominant, count: max, total: regimes.length };
  }, [regimes, regimeCounts]);

  // Trade filter state
  const [tradeFilter, setTradeFilter] = useState<'all' | 'demo' | 'live' | 'manual'>('all');

  // Open Positions — ALL positions, no limit
  const { data: openPositionsData } = useQuery({
    queryKey: ['dashboard-positions', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data } = await supabase
        .from('strategy_positions')
        .select('id, symbol, side, quantity, average_entry_price, unrealized_pnl, status, strategy_instance_id')
        .eq('user_id', user.id)
        .eq('status', 'open')
        .order('opened_at', { ascending: false });
      return data ?? [];
    },
    enabled: !!user?.id,
    refetchInterval: 10000,
  });

  // Strategy instances for categorization
  const { data: strategyInstances } = useQuery({
    queryKey: ['strategy-instances-map', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data } = await supabase
        .from('strategy_instances')
        .select('id, execution_mode, strategy_name')
        .eq('user_id', user.id);
      return data ?? [];
    },
    enabled: !!user?.id,
  });

  const instanceMap = useMemo(() => {
    const map: Record<string, { execution_mode: string; strategy_name: string }> = {};
    for (const si of strategyInstances ?? []) {
      map[si.id] = { execution_mode: si.execution_mode, strategy_name: si.strategy_name };
    }
    return map;
  }, [strategyInstances]);

  const getTradeCategory = (strategyInstanceId: string | null): 'demo' | 'live' | 'manual' => {
    if (!strategyInstanceId) return 'manual';
    const inst = instanceMap[strategyInstanceId];
    if (!inst) return 'demo';
    if (inst.strategy_name === 'manual-trading') return 'manual';
    return inst.execution_mode === 'live' ? 'live' : 'demo';
  };

  // Live market prices for open position PnL
  // Normalize symbols: positions may use BTCUSD but market_prices uses BTC/USD
  const normalizeSymbol = (s: string): string => {
    if (s.includes('/')) return s;
    if (s.length === 6) return s.slice(0, 3) + '/' + s.slice(3);
    if (s.length === 7) return s.slice(0, 4) + '/' + s.slice(4);
    return s;
  };

  const positionSymbols = useMemo(() => {
    if (!openPositionsData || openPositionsData.length === 0) return [];
    const raw = openPositionsData.map(p => p.symbol);
    const normalized = raw.map(normalizeSymbol);
    return [...new Set([...raw, ...normalized])];
  }, [openPositionsData]);

  const { data: livePriceMap } = useQuery({
    queryKey: ['market-prices-for-positions', positionSymbols],
    queryFn: async () => {
      const { data } = await supabase.from('market_prices').select('symbol, price').in('symbol', positionSymbols);
      const map: Record<string, number> = {};
      for (const p of data ?? []) {
        const price = Number(p.price);
        map[p.symbol] = price;
        // Also map the non-slash version so position lookups work
        const noSlash = p.symbol.replace('/', '');
        map[noSlash] = price;
      }
      return map;
    },
    refetchInterval: 5000,
    enabled: positionSymbols.length > 0,
  });

  // Filtered positions & orders
  const filteredPositions = useMemo(() => {
    if (!openPositionsData) return [];
    if (tradeFilter === 'all') return openPositionsData;
    return openPositionsData.filter(p => getTradeCategory(p.strategy_instance_id) === tradeFilter);
  }, [openPositionsData, tradeFilter, instanceMap]);

  // Recent Orders — fetch more, display 5
  const { data: recentOrders } = useQuery({
    queryKey: ['recent-orders', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data } = await supabase
        .from('strategy_orders')
        .select('id, symbol, side, quantity, price, status, created_at, strategy_instance_id')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(20);
      return data ?? [];
    },
    enabled: !!user?.id,
    refetchInterval: 30000,
  });

  const filteredOrders = useMemo(() => {
    if (!recentOrders) return [];
    if (tradeFilter === 'all') return recentOrders;
    return recentOrders.filter(o => getTradeCategory(o.strategy_instance_id) === tradeFilter);
  }, [recentOrders, tradeFilter, instanceMap]);

  // Use notifications hook
  const { unreadCount } = useNotifications();

  const latestSnapshot = snapshots.length > 0 ? snapshots[snapshots.length - 1] : null;
  const allocationData = useMemo(() => {
    if (!latestSnapshot?.allocation_by_class) return [];
    const alloc = latestSnapshot.allocation_by_class as Record<string, number>;
    return Object.entries(alloc).map(([name, value]) => ({ name, value: Number(value) }));
  }, [latestSnapshot]);

  const totalInvested = allocationData.reduce((sum, d) => sum + d.value, 0);

  const heldSymbols = useMemo(() => {
    if (!openPositionsData || openPositionsData.length < 3) return undefined;
    return [...new Set(openPositionsData.map(p => p.symbol))].slice(0, 6);
  }, [openPositionsData]);

  const pnlChangePercent = totalBalance > 0 ? ((totalPnl / totalBalance) * 100) : 0;

  // Sparkline data from snapshots
  const sparklineData = useMemo(() => {
    if (snapshots.length === 0) return [];
    const recent = snapshots.slice(-20);
    return recent.map(s => ({ v: Number(s.total_equity) }));
  }, [snapshots]);

  // Circuit breaker
  const circuitBreakerStatus = useMemo(() => {
    if (!snapshots || snapshots.length < 2) return { level: 'normal' as const, drawdown: 0 };
    const todayStart = new Date();
    todayStart.setUTCHours(0, 0, 0, 0);
    const todaySnapshots = snapshots.filter(s => new Date(s.snapshot_at ?? '') >= todayStart);
    if (todaySnapshots.length < 2) return { level: 'normal' as const, drawdown: 0 };
    let peak = 0;
    for (const s of todaySnapshots) {
      const eq = Number(s.total_equity);
      if (eq > peak) peak = eq;
    }
    const current = Number(todaySnapshots[todaySnapshots.length - 1].total_equity);
    const dd = peak > 0 ? (peak - current) / peak : 0;
    if (dd > 0.05) return { level: 'halt' as const, drawdown: dd };
    if (dd > 0.03) return { level: 'warning' as const, drawdown: dd };
    return { level: 'normal' as const, drawdown: dd };
  }, [snapshots]);

  const [resuming, setResuming] = useState(false);
  const [exporting, setExporting] = useState(false);

  const handleResumeTrading = async () => {
    if (!user?.id || circuitBreakerStatus.drawdown > 0.03) return;
    setResuming(true);
    try {
      const { error } = await supabase
        .from('strategy_instances')
        .update({ status: 'running' })
        .eq('user_id', user.id)
        .eq('status', 'paused');
      if (error) throw error;
      toast({ title: 'Trading resumed', description: 'Paused strategies are now running.' });

      // Force a fresh portfolio snapshot so the circuit-breaker memo recomputes
      // with current data instead of waiting for the next 15-min cron tick.
      try {
        await supabase.functions.invoke('snapshot-portfolio', { body: { user_id: user.id } });
      } catch {
        // non-fatal
      }

      // Refetch (not just invalidate) so the UI updates synchronously.
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ['strategy-instances-map', user.id] }),
        queryClient.refetchQueries({ queryKey: ['portfolio-health', user.id] }),
        queryClient.refetchQueries({ queryKey: ['portfolio-snapshots', user.id] }),
      ]);
    } catch {
      toast({ title: 'Failed to resume trading', variant: 'destructive' });
    } finally {
      setResuming(false);
    }
  };

  const handleExportReport = async () => {
    setExporting(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-report', { body: { report_type: 'portfolio' } });
      if (error) throw error;
      const blob = new Blob([data.html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const win = window.open(url, '_blank');
      if (win) { win.onload = () => { win.print(); URL.revokeObjectURL(url); }; }
    } catch (err) { console.error('Export failed:', err); }
    finally { setExporting(false); }
  };

  const runningCount = strategies.filter(s => s.status === 'running').length;
  const pausedCount = strategies.filter(s => s.status === 'paused').length;
  const compoundPnl = formatPnl(totalPnl);
  const maxPositionValue = totalBalance * 0.05;
  const dailyDDPercent = circuitBreakerStatus.drawdown * 100;

  const latestSignal = pendingSignals.length > 0 ? pendingSignals[0] : null;

  const isLoading = summaryLoading || strategiesLoading;

  const displayedTrades = filteredOrders?.slice(0, 5) ?? [];
  const hasMoreTrades = (filteredOrders?.length ?? 0) > 5;
  const posCount = openPositionsData?.length ?? 0;

  // formatQty imported from @/utils/formatters

  const isFetching = useIsFetching();

  return (
    <div className="flex flex-col h-full lg:h-[calc(100vh-64px)]">
      {/* Refetch progress bar */}
      {isFetching > 0 && <div className="refetch-bar flex-shrink-0" />}

      {/* Header */}
      <div className="flex items-center justify-between px-1 py-2 flex-shrink-0">
        <h1 className="text-lg font-bold tracking-tight font-heading">Command Center</h1>
        <div className="flex items-center gap-2">
          <div className="lg:hidden relative">
            <Bell className="h-5 w-5 text-muted-foreground" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[hsl(var(--accent-red))] text-[9px] font-bold text-white flex items-center justify-center">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </div>
          <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={handleExportReport} disabled={exporting}>
            <FileText className="h-3.5 w-3.5" />
            {exporting ? 'Generating...' : 'Export'}
          </Button>
        </div>
      </div>

      {/* Circuit Breaker Banners */}
      {circuitBreakerStatus.level === 'halt' && (
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="flex-shrink-0 mb-2">
          <div className="flex items-start gap-3 rounded-xl px-4 py-3 text-sm bg-[hsl(var(--accent-red)/0.05)] border border-[hsl(var(--accent-red)/0.15)]">
            <ShieldAlert className="w-5 h-5 mt-0.5 flex-shrink-0 text-[hsl(var(--accent-red))]" />
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm text-[hsl(var(--accent-red))]">CIRCUIT BREAKER ACTIVE</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Trading paused — portfolio down {(circuitBreakerStatus.drawdown * 100).toFixed(1)}% from today's peak.
              </p>
              <Button size="sm" variant="outline" className="mt-2 h-7 text-xs border-[hsl(var(--accent-red)/0.3)]"
                disabled={circuitBreakerStatus.drawdown > 0.03 || resuming} onClick={handleResumeTrading}>
                <Play className="h-3 w-3 mr-1" />
                {circuitBreakerStatus.drawdown > 0.03 ? 'Recover below 3%' : resuming ? 'Resuming...' : 'Resume Trading'}
              </Button>
            </div>
          </div>
        </motion.div>
      )}
      {circuitBreakerStatus.level === 'warning' && (
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="flex-shrink-0 mb-2">
          <div className="flex items-start gap-3 rounded-xl px-4 py-3 text-sm bg-[hsl(var(--accent-amber)/0.05)] border border-[hsl(var(--accent-amber)/0.15)]">
            <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0 text-[hsl(var(--accent-amber))]" />
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm">Drawdown Warning</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Portfolio down {(circuitBreakerStatus.drawdown * 100).toFixed(1)}% from today's peak.
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {/* ═══ 3-Column Grid — fills remaining viewport ═══ */}
      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr_240px] gap-3 flex-1 min-h-0 overflow-y-auto lg:overflow-hidden">

        {/* ─── LEFT COLUMN ─── */}
        <div className="space-y-3 lg:overflow-y-auto lg:pr-1 scrollbar-thin">

          {/* Account Summary */}
          <TCard accent={totalPnl >= 0 ? 'profit' : 'loss'} className="p-3">
            {isLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-8 w-32" />
                <Skeleton className="h-4 w-20" />
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Portfolio Value</span>
                  <Badge variant="outline" className="text-[9px] h-4 gap-1 border-[hsl(var(--accent-blue)/0.3)] text-[hsl(var(--accent-blue))]">
                    <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--accent-blue))] animate-pulse" />
                    Demo
                  </Badge>
                </div>
                <div className="flex items-end gap-3">
                  <span className="font-mono-data text-[26px] font-bold leading-none">
                    ${totalBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                  {sparklineData.length > 2 && (
                    <div className="w-[56px] h-[22px] mb-1">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={sparklineData}>
                          <Line type="monotone" dataKey="v" stroke={totalPnl >= 0 ? 'hsl(142, 71%, 45%)' : 'hsl(0, 84%, 60%)'} strokeWidth={1.5} dot={false} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`font-mono-data text-sm font-semibold ${totalPnl >= 0 ? 'text-profit' : 'text-loss'}`}>
                    {formatPnl(totalPnl)}
                  </span>
                  <span className={`text-[10px] font-medium ${totalPnl >= 0 ? 'text-profit' : 'text-loss'}`}>
                    {pnlChangePercent >= 0 ? '▲' : '▼'} {Math.abs(pnlChangePercent).toFixed(1)}%
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2 mt-2 pt-2 border-t border-[hsl(var(--card-border))]">
                  <div>
                    <span className="text-[9px] text-muted-foreground block">Strategies</span>
                    <div className="flex items-center gap-1.5">
                      <span className="font-mono-data text-sm font-semibold">{activeCount}</span>
                      <div className="flex items-center gap-0.5">
                        {runningCount > 0 && <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--accent-green))]" title={`${runningCount} running`} />}
                        {pausedCount > 0 && <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40" title={`${pausedCount} paused`} />}
                      </div>
                    </div>
                  </div>
                  <div>
                    <span className="text-[9px] text-muted-foreground block">Positions</span>
                    <div className="flex items-center gap-1.5">
                      <span className="font-mono-data text-sm font-semibold">{posCount}</span>
                      {openPositionsData && openPositionsData.length > 0 && (() => {
                        const longs = openPositionsData.filter(p => p.side === 'buy').length;
                        const shorts = openPositionsData.length - longs;
                        const longPct = (longs / openPositionsData.length) * 100;
                        return (
                          <div className="w-[24px] h-[6px] rounded-full bg-[hsl(var(--accent-red)/0.3)] overflow-hidden" title={`${longs}L / ${shorts}S`}>
                            <div className="h-full bg-[hsl(var(--accent-green)/0.7)] rounded-full" style={{ width: `${longPct}%` }} />
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                  <div>
                    <span className="text-[9px] text-muted-foreground block">Trades</span>
                    <div className="flex items-center gap-1.5">
                      <span className="font-mono-data text-sm font-semibold">{todayTrades}</span>
                      {recentOrders && recentOrders.length > 0 && (
                        <div className="flex items-center gap-0.5">
                          {recentOrders.slice(0, 5).map((o, i) => (
                            <span key={i} className={`w-1.5 h-1.5 rounded-full ${o.side === 'buy' ? 'bg-[hsl(var(--accent-green)/0.6)]' : 'bg-[hsl(var(--accent-red)/0.6)]'}`} />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </TCard>

          {/* Risk Engine — hidden on mobile */}
          <TCard className="p-3 hidden lg:block">
            <div className="flex items-center gap-2 mb-2">
              <Shield className="h-4 w-4 text-muted-foreground" />
              <span className="text-xs font-semibold font-heading">Risk Engine</span>
              <span className={`w-2 h-2 rounded-full ml-auto ${
                circuitBreakerStatus.level === 'normal' ? 'bg-[hsl(var(--accent-green))]' :
                circuitBreakerStatus.level === 'warning' ? 'bg-[hsl(var(--accent-amber))]' :
                'bg-[hsl(var(--accent-red))]'
              }`} />
            </div>
            <div className="space-y-2">
              <div>
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-[10px] text-muted-foreground">Max Position Size</span>
                  <span className="text-[10px] font-mono-data text-muted-foreground">5% (${maxPositionValue.toFixed(0)})</span>
                </div>
                <Progress value={35} className="h-1" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-[10px] text-muted-foreground">Open Positions</span>
                   <span className="text-[10px] font-mono-data text-muted-foreground">{posCount}/10</span>
                </div>
                <Progress value={(posCount / 10) * 100} className="h-1" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-[10px] text-muted-foreground">Daily Drawdown</span>
                  <span className="text-[10px] font-mono-data text-muted-foreground">{dailyDDPercent.toFixed(1)}%/10%</span>
                </div>
                <Progress
                  value={(dailyDDPercent / 10) * 100}
                  className={`h-1 ${dailyDDPercent > 5 ? '[&>div]:bg-[hsl(var(--accent-red))]' : dailyDDPercent > 3 ? '[&>div]:bg-[hsl(var(--accent-amber))]' : ''}`}
                />
              </div>
              <div className="flex items-center justify-between pt-1">
                <span className="text-[10px] text-muted-foreground">Circuit Breaker</span>
                <Badge variant="outline" className={`text-[9px] h-4 ${
                  circuitBreakerStatus.level === 'normal' ? 'border-[hsl(var(--accent-green)/0.3)] text-[hsl(var(--accent-green))]' :
                  circuitBreakerStatus.level === 'warning' ? 'border-[hsl(var(--accent-amber)/0.3)] text-[hsl(var(--accent-amber))]' :
                  'border-[hsl(var(--accent-red)/0.3)] text-[hsl(var(--accent-red))]'
                }`}>
                  {circuitBreakerStatus.level.toUpperCase()}
                </Badge>
              </div>
            </div>
          </TCard>

          {/* AI Engine Status — hidden on mobile */}
          <TCard accent="purple" className="p-3 hidden lg:block">
            <div className="flex items-center gap-2 mb-2">
              <Brain className="h-4 w-4 text-[hsl(var(--accent-purple))]" />
              <span className="text-xs font-semibold font-heading">AI Signal Engine</span>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-muted-foreground">Signals Today</span>
                <span className="font-mono-data text-xs font-semibold">{recentSignals.length}</span>
              </div>
              <div
                onClick={() => onPageChange?.('signals')}
                className="flex items-center justify-between cursor-pointer hover:bg-[hsl(var(--background-tertiary))] rounded-md px-1 py-0.5 -mx-1 transition-colors"
              >
                <span className="text-[10px] text-muted-foreground">Pending Review</span>
                <span className="font-mono-data text-xs font-semibold text-[hsl(var(--accent-purple))]">{pendingSignals.length}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-muted-foreground">Next Run</span>
                <span className="font-mono-data text-xs font-semibold">{String(countdownMin).padStart(2, '0')}:{String(countdownSec).padStart(2, '0')}</span>
              </div>
              {latestSignal && (
                <div
                  onClick={() => onPageChange?.('signals')}
                  className="mt-1 pt-1.5 border-t border-[hsl(var(--card-border))] cursor-pointer hover:bg-[hsl(var(--background-tertiary))] rounded-md px-1 py-0.5 -mx-1 transition-colors"
                >
                  <span className="text-[9px] text-muted-foreground block mb-1">Latest Signal</span>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className={`text-[9px] h-4 ${latestSignal.side === 'buy' ? 'text-profit border-[hsl(var(--accent-green)/0.3)]' : 'text-loss border-[hsl(var(--accent-red)/0.3)]'}`}>
                      {latestSignal.side.toUpperCase()}
                    </Badge>
                    <span className="text-xs font-medium">{latestSignal.symbol}</span>
                    <span className="text-[10px] font-mono-data text-muted-foreground ml-auto">
                      {(latestSignal.confidence * 100).toFixed(0)}%
                    </span>
                  </div>
                </div>
              )}
            </div>
          </TCard>
        </div>

        {/* ─── CENTER COLUMN ─── */}
        <div className="space-y-3 lg:overflow-y-auto lg:pr-1 scrollbar-thin">

          {/* 1. Market Ticker Strip */}
          <TCard className="p-0 overflow-hidden flex-shrink-0">
            <div
              className="overflow-x-auto scrollbar-none"
              onScroll={() => {
                if (typeof window !== 'undefined' && !localStorage.getItem('ticker-swiped')) {
                  localStorage.setItem('ticker-swiped', '1');
                }
              }}
            >
              <div className="flex min-w-max">
                {marketLoading ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="px-3 py-2 border-r border-[hsl(var(--card-border))]">
                      <Skeleton className="h-3 w-12 mb-1" />
                      <Skeleton className="h-4 w-16" />
                    </div>
                  ))
                ) : marketPrices.length > 0 ? (
                  marketPrices.slice(0, 12).map((mp) => (
                    <button
                      key={mp.symbol}
                      onClick={() => navigate('/trading-zone', { state: { symbol: mp.symbol } })}
                      className="px-3 py-2 border-r border-[hsl(var(--card-border))] hover:bg-[hsl(var(--background-tertiary))] transition-colors flex-shrink-0 text-left"
                    >
                      <div className="text-[9px] uppercase tracking-wider text-muted-foreground mb-0.5">{mp.symbol}</div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs font-mono-data font-bold">
                          ${mp.price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}
                        </span>
                        <span className={`text-[10px] font-mono-data font-medium ${mp.change_percent >= 0 ? 'text-profit' : 'text-loss'}`}>
                          {mp.change_percent >= 0 ? '+' : ''}{mp.change_percent.toFixed(2)}%
                        </span>
                      </div>
                    </button>
                  ))
                ) : (
                  <div className="px-3 py-2 text-xs text-muted-foreground">No market data</div>
                )}
              </div>
            </div>
            {/* Swipe hint — mobile only, hidden after first swipe */}
            {typeof window !== 'undefined' && !localStorage.getItem('ticker-swiped') && (
              <div className="lg:hidden flex justify-end px-3 pb-1">
                <span className="text-[10px] text-muted-foreground/50">Swipe →</span>
              </div>
            )}
          </TCard>

          {/* Trade Filter Toggle */}
          <div className="flex items-center gap-1 px-1">
            {(['all', 'demo', 'live', 'manual'] as const).map(f => (
              <button
                key={f}
                onClick={() => setTradeFilter(f)}
                className={`px-2.5 py-1 rounded-md text-[10px] font-medium transition-colors ${
                  tradeFilter === f
                    ? 'bg-[hsl(var(--accent-purple)/0.15)] text-[hsl(var(--accent-purple))]'
                    : 'text-muted-foreground hover:bg-[hsl(var(--background-tertiary))]'
                }`}
              >
                {f === 'all' ? 'All' : f === 'demo' ? 'Demo' : f === 'live' ? 'Live' : 'Manual'}
              </button>
            ))}
          </div>

          {/* 2. Active Strategies */}
          <TCard className="p-0">
            <div className="flex items-center justify-between px-3 py-2 border-b border-[hsl(var(--card-border))]">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold font-heading">Active Strategies</span>
                <Badge variant="outline" className="text-[9px] h-4 border-[hsl(var(--card-border))]">{strategies.length}</Badge>
                <HeartbeatIndicator status={strategies.length > 0 ? 'healthy' : 'disconnected'} />
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0"><MoreHorizontal className="h-3.5 w-3.5" /></Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52 bg-popover border-border/50">
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground/70">Actions</DropdownMenuLabel>
                  <DropdownMenuItem onSelect={(e) => { e.preventDefault(); handlePauseAll(); }} disabled={pausing}>
                    <Pause className="h-3.5 w-3.5 mr-2" /> {pausing ? 'Pausing…' : 'Pause All'}
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={(e) => { e.preventDefault(); handleCloseAllPositions(); }} disabled={closingAll}>
                    <DollarSign className="h-3.5 w-3.5 mr-2" /> {closingAll ? 'Closing…' : 'Close All Positions'}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="text-[hsl(var(--accent-amber))]"
                    onSelect={(e) => { e.preventDefault(); setShowKillDialog(true); }}
                  >
                    <ShieldAlert className="h-3.5 w-3.5 mr-2" /> Emergency Kill Switch
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {strategies.length > 0 ? (
              <>
                <div className="hidden lg:grid grid-cols-[1.4fr_80px_60px_100px_90px_36px] items-center px-3 py-1.5 text-[9px] uppercase tracking-wider font-medium text-muted-foreground border-b border-[hsl(var(--card-border)/0.5)]">
                  <span>Strategy</span>
                  <span>Symbol</span>
                  <span>Mode</span>
                  <span>Today P&L</span>
                  <span>Status</span>
                  <span />
                </div>

                <div className="max-h-[160px] overflow-y-auto scrollbar-thin">
                  {strategies.map((strategy) => {
                    const perf = strategy.strategy_performance?.[0];
                    const netProfit = perf?.net_profit ?? 0;
                    const isRunning = strategy.status === 'running';
                    return (
                      <StrategyCompactRow key={strategy.id} strategy={strategy} netProfit={netProfit} isRunning={isRunning} />
                    );
                  })}
                </div>

                <div className="flex items-center justify-between px-3 py-2 border-t border-[hsl(var(--card-border))]">
                  <span className="text-[10px] text-muted-foreground">{runningCount} running · {pausedCount} paused</span>
                  <span className={`font-mono-data text-sm font-bold ${totalPnl >= 0 ? 'text-profit' : 'text-loss'}`}>{compoundPnl}</span>
                </div>
              </>
            ) : (
              <EmptyState
                icon={Rocket}
                title="No active strategies"
                description="Deploy a strategy to start automated trading."
                actionLabel="Browse Strategies"
                onAction={() => navigate('/strategies')}
              />
            )}
          </TCard>

          {/* 3. Open Positions */}
          <TCard className="p-0">
            <div className="px-3 py-2 border-b border-[hsl(var(--card-border))]">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold font-heading">Open Positions</span>
                <Badge variant="outline" className="text-[9px] h-4 border-[hsl(var(--card-border))]">
                  {filteredPositions.length}
                </Badge>
              </div>
            </div>
            {filteredPositions.length > 0 ? (
              <div className="divide-y divide-[hsl(var(--card-border)/0.5)] max-h-[200px] overflow-y-auto scrollbar-thin">
                {filteredPositions.map((pos) => {
                  const isLong = pos.side === 'buy' || pos.side === 'long';
                  const currentPrice = livePriceMap?.[pos.symbol] ?? Number(pos.average_entry_price);
                  const direction = isLong ? 1 : -1;
                  const unrealizedPnl = (currentPrice - Number(pos.average_entry_price)) * Number(pos.quantity) * direction;
                  const category = getTradeCategory(pos.strategy_instance_id);
                  return (
                    <div key={pos.id} className="flex items-center justify-between px-3 py-2 hover:bg-[hsl(var(--background-tertiary))] transition-colors">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className={`text-[9px] h-4 uppercase ${
                          isLong ? 'text-profit border-[hsl(var(--accent-green)/0.3)]' : 'text-loss border-[hsl(var(--accent-red)/0.3)]'
                        }`}>
                          {isLong ? 'LONG' : 'SHORT'}
                        </Badge>
                        <span className="text-xs font-medium">{pos.symbol}</span>
                        <span className="text-[10px] text-muted-foreground font-mono-data">×{formatQty(Number(pos.quantity))}</span>
                        <Badge variant="outline" className={`text-[8px] h-3.5 ${
                          category === 'demo' ? 'border-[hsl(var(--accent-green)/0.3)] text-[hsl(var(--accent-green))]' :
                          category === 'live' ? 'border-[hsl(var(--accent-red)/0.3)] text-[hsl(var(--accent-red))]' :
                          'border-[hsl(var(--accent-blue)/0.3)] text-[hsl(var(--accent-blue))]'
                        }`}>
                          {category.toUpperCase()}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-muted-foreground font-mono-data">${formatPrice(currentPrice)}</span>
                        <span className={`text-xs font-mono-data font-semibold ${unrealizedPnl >= 0 ? 'text-profit' : 'text-loss'}`}>
                          {formatPnl(unrealizedPnl)}
                        </span>
                        <button
                          onClick={() => navigate('/trading-zone', { state: { symbol: pos.symbol } })}
                          className="h-5 w-5 rounded flex items-center justify-center hover:bg-[hsl(var(--background-secondary))] text-muted-foreground hover:text-foreground transition-colors"
                          title="Open in Trading Zone"
                        >
                          <BarChart3 className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="px-3 py-3 text-xs text-muted-foreground">No open positions</div>
            )}
          </TCard>

          {/* 4. Recent Trades (max 5) */}
          <TCard className="p-0">
            <div className="px-3 py-2 border-b border-[hsl(var(--card-border))]">
              <span className="text-sm font-semibold font-heading">Recent Trades</span>
            </div>
            {displayedTrades.length > 0 ? (
              <>
                <div className="divide-y divide-[hsl(var(--card-border)/0.5)] max-h-[180px] overflow-y-auto scrollbar-thin">
                  {displayedTrades.map((order) => {
                    const category = getTradeCategory(order.strategy_instance_id);
                    return (
                      <div key={order.id} className="flex items-center justify-between px-3 py-2 hover:bg-[hsl(var(--background-tertiary))] transition-colors">
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] text-muted-foreground font-mono-data w-14">
                            {formatTimeAgo(order.created_at)}
                          </span>
                          <Badge variant="outline" className={`text-[9px] h-4 uppercase ${
                            order.side === 'buy' ? 'text-profit border-[hsl(var(--accent-green)/0.3)]' : 'text-loss border-[hsl(var(--accent-red)/0.3)]'
                          }`}>
                            {order.side}
                          </Badge>
                          <span className="text-xs font-medium">{order.symbol}</span>
                          <span className="text-[10px] text-muted-foreground font-mono-data">×{formatQty(Number(order.quantity))}</span>
                          <Badge variant="outline" className={`text-[8px] h-3.5 ${
                            category === 'demo' ? 'border-[hsl(var(--accent-green)/0.3)] text-[hsl(var(--accent-green))]' :
                            category === 'live' ? 'border-[hsl(var(--accent-red)/0.3)] text-[hsl(var(--accent-red))]' :
                            'border-[hsl(var(--accent-blue)/0.3)] text-[hsl(var(--accent-blue))]'
                          }`}>
                            {category.toUpperCase()}
                          </Badge>
                        </div>
                        {order.price && (
                          <span className="text-xs font-mono-data">${formatPrice(Number(order.price))}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
                {hasMoreTrades && (
                  <div className="px-3 py-2 border-t border-[hsl(var(--card-border))]">
                    <button
                      onClick={() => onPageChange?.('journal')}
                      className="text-[11px] text-[hsl(var(--accent-blue))] hover:underline"
                    >
                      View all in Journal →
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="px-3 py-3 text-xs text-muted-foreground">No trades yet</div>
            )}
          </TCard>
        </div>

        {/* ─── RIGHT COLUMN (hidden mobile) ─── */}
        <div className="hidden lg:flex flex-col gap-3 lg:overflow-y-auto lg:pr-1 scrollbar-thin">

          {/* Onboarding Checklist */}
          {onPageChange && <OnboardingChecklist onPageChange={onPageChange} />}

          {/* 1. Market Regime */}
          <TCard className="p-3">
            <div className="flex items-center gap-2 mb-2">
              <Gauge className="h-4 w-4 text-muted-foreground" />
              <span className="text-xs font-semibold font-heading">Market Regime</span>
            </div>
            {dominantRegime ? (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <span className={`text-base font-bold font-heading ${
                    dominantRegime.regime === 'trending' ? 'text-profit' :
                    dominantRegime.regime === 'volatile' ? 'text-[hsl(var(--accent-amber))]' :
                    dominantRegime.regime === 'crisis' ? 'text-loss' : 'text-muted-foreground'
                  }`}>
                    {dominantRegime.regime.toUpperCase()}
                  </span>
                </div>
                <div className="space-y-1.5">
                  {Object.entries(regimeCounts).map(([regime, count]) => (
                    <div key={regime}>
                      <div className="flex items-center justify-between mb-0.5">
                        <span className="text-[10px] capitalize text-muted-foreground">{regime}</span>
                        <span className="text-[10px] font-mono-data text-muted-foreground">{count}</span>
                      </div>
                      <div className="w-full h-1 rounded-full bg-[hsl(var(--background-tertiary))]">
                        <div
                          className={`h-full rounded-full transition-all ${
                            regime === 'trending' ? 'bg-[hsl(var(--accent-green))]' :
                            regime === 'ranging' ? 'bg-[hsl(var(--accent-blue))]' :
                            regime === 'volatile' ? 'bg-[hsl(var(--accent-amber))]' :
                            'bg-[hsl(var(--accent-red))]'
                          }`}
                          style={{ width: `${regimes && regimes.length > 0 ? (count / regimes.length) * 100 : 0}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <p className="text-xs text-muted-foreground">No regime data</p>
            )}
          </TCard>

          {/* 2. Notification Center */}
          <NotificationCenter />

          {/* 3. Portfolio Health (compact) */}
          <TCard className="p-3">
            <div className="flex justify-center">
              <div className="w-[80px] h-[80px]">
                <HealthScoreGauge score={health?.health_score ?? 100} isLoading={healthLoading} />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 mt-2 pt-2 border-t border-[hsl(var(--card-border))]">
              <div className="text-center">
                <span className="text-[9px] text-muted-foreground block">Sharpe</span>
                <span className="font-mono-data text-xs font-semibold">{health?.sharpe_ratio != null ? Number(health.sharpe_ratio).toFixed(2) : '—'}</span>
              </div>
              <div className="text-center">
                <span className="text-[9px] text-muted-foreground block">DD%</span>
                <span className="font-mono-data text-xs font-semibold">{health?.max_drawdown != null ? `${(Number(health.max_drawdown) * 100).toFixed(1)}%` : '—'}</span>
              </div>
              <div className="text-center">
                <span className="text-[9px] text-muted-foreground block">VaR</span>
                <span className="font-mono-data text-xs font-semibold">{health?.var_95 != null ? `${(Number(health.var_95) * 100).toFixed(1)}%` : '—'}</span>
              </div>
            </div>
          </TCard>

          {/* 4. Allocation Mini Pie — only if invested */}
          {totalInvested > 0 && (
            <TCard className="p-3">
              <span className="text-[10px] text-muted-foreground uppercase tracking-wider block mb-2">Allocation</span>
              <div className="flex justify-center">
                <div className="w-[100px] h-[100px]">
                  <AllocationPieChart
                    allocationByClass={latestSnapshot?.allocation_by_class as Record<string, number> | null}
                    isLoading={healthLoading}
                  />
                </div>
              </div>
              {allocationData.length > 0 && (
                <div className="mt-2 space-y-1">
                  {allocationData.map(item => (
                    <div key={item.name} className="flex items-center justify-between text-[10px]">
                      <span className="text-muted-foreground">{item.name}</span>
                      <span className="font-mono-data">{totalInvested > 0 ? ((item.value / totalInvested) * 100).toFixed(0) : 0}%</span>
                    </div>
                  ))}
                </div>
              )}
            </TCard>
          )}

          {/* Correlation Alert */}
          {health?.avg_correlation != null && Number(health.avg_correlation) > 0.5 && (
            <TCard accent="loss" className="p-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-3.5 w-3.5 text-[hsl(var(--accent-amber))] mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-[11px] font-medium">High Correlation</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    Avg correlation {Number(health.avg_correlation).toFixed(2)} — consider diversifying held assets.
                  </p>
                </div>
              </div>
              {heldSymbols && (
                <div className="mt-2">
                  <CorrelationHeatmap heldSymbols={heldSymbols} isLoading={false} />
                </div>
              )}
            </TCard>
          )}
        </div>
      </div>

      {/* Mobile-only: Portfolio Health */}
      <div className="lg:hidden mt-3">
        <TCard className="p-3">
          <div className="flex justify-center">
            <HealthScoreGauge score={health?.health_score ?? 100} isLoading={healthLoading} />
          </div>
        </TCard>
      </div>

      <AlertDialog open={showKillDialog} onOpenChange={setShowKillDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <ShieldAlert className="h-5 w-5" /> Emergency Kill Switch
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will halt all active strategies, cancel pending orders, and prevent any new
              trades from being placed. You can resume trading manually afterward. Continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={killing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleEmergencyKill}
              disabled={killing}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {killing ? 'Stopping…' : 'Stop All Trading'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

/* ─── Heartbeat Indicator ─── */
type HeartbeatStatus = 'healthy' | 'delayed' | 'paused' | 'disconnected';
const heartbeatConfig: Record<HeartbeatStatus, { label: string; className: string }> = {
  healthy: { label: 'Live', className: 'heartbeat-healthy' },
  delayed: { label: 'Delayed', className: 'heartbeat-delayed' },
  paused: { label: 'Paused', className: 'heartbeat-paused' },
  disconnected: { label: 'Offline', className: 'heartbeat-disconnected' },
};

const HeartbeatIndicator = ({ status }: { status: HeartbeatStatus }) => {
  const config = heartbeatConfig[status];
  const textColor = status === 'healthy' ? 'text-[hsl(var(--success)/0.7)]'
    : status === 'delayed' ? 'text-[hsl(var(--warning)/0.7)]'
    : status === 'paused' ? 'text-muted-foreground/60'
    : 'text-destructive/50';

  return (
    <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-md border border-[hsl(var(--card-border))] bg-[hsl(var(--background-tertiary))]`}>
      <div className={`w-1.5 h-1.5 rounded-full ${config.className}`} />
      <span className={`text-[9px] font-medium ${textColor}`}>{config.label}</span>
    </div>
  );
};

/* ─── Compact Strategy Row ─── */
const StrategyCompactRow = ({ strategy, netProfit, isRunning }: { strategy: ActiveStrategy; netProfit: number; isRunning: boolean }) => {
  const [expanded, setExpanded] = useState(false);
  const Icon = getStrategyIcon(strategy.strategy_name);
  const perf = strategy.strategy_performance?.[0];

  return (
    <div className={`border-l-2 ${isRunning ? 'border-l-[hsl(var(--accent-green))]' : 'border-l-transparent'} ${!isRunning ? 'opacity-60' : ''}`}>
      {/* Desktop */}
      <div className="hidden lg:grid grid-cols-[1.4fr_80px_60px_100px_90px_36px] items-center px-4 py-2.5 hover:bg-[hsl(var(--background-tertiary))] transition-colors border-b border-[hsl(var(--card-border)/0.3)]">
        <div className="flex items-center gap-2 min-w-0">
          <Icon className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
          <span className="text-xs font-medium truncate">{strategy.strategy_name}</span>
        </div>
        <span className="text-[10px] text-muted-foreground font-mono-data">—</span>
        <Badge variant="outline" className="text-[8px] h-3.5 border-[hsl(var(--card-border))]">DEMO</Badge>
        <span className={`text-xs font-mono-data font-semibold ${netProfit >= 0 ? 'text-profit' : 'text-loss'}`}>
          {formatPnl(netProfit)}
        </span>
        <div className="flex items-center gap-1">
          <span className={`w-1.5 h-1.5 rounded-full ${isRunning ? 'heartbeat-healthy' : 'heartbeat-paused'}`} />
          <span className="text-[10px] text-muted-foreground">{isRunning ? 'Running' : 'Paused'}</span>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="h-6 w-6 rounded flex items-center justify-center hover:bg-[hsl(var(--background-tertiary))]">
              <MoreHorizontal className="h-3 w-3 text-muted-foreground" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48 bg-popover">
            <DropdownMenuItem><Eye className="h-3.5 w-3.5 mr-2" /> View Details</DropdownMenuItem>
            <DropdownMenuItem><Pause className="h-3.5 w-3.5 mr-2" /> Pause</DropdownMenuItem>
            <DropdownMenuItem className="text-[hsl(var(--accent-red))]"><Square className="h-3.5 w-3.5 mr-2" /> Stop</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Mobile */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="lg:hidden w-full flex items-center gap-3 px-4 py-3 hover:bg-[hsl(var(--background-tertiary))] transition-colors text-left border-b border-[hsl(var(--card-border)/0.3)]"
      >
        <Icon className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <span className="text-xs font-medium truncate block">{strategy.strategy_name}</span>
          <div className="flex items-center gap-2 mt-0.5">
            <span className={`text-[10px] font-mono-data font-semibold ${netProfit >= 0 ? 'text-profit' : 'text-loss'}`}>
              {formatPnl(netProfit)}
            </span>
            <span className="text-[9px] text-muted-foreground">{perf?.total_trades ?? 0} trades</span>
          </div>
        </div>
        <ChevronDown className={`h-3 w-3 text-muted-foreground transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>
      {expanded && (
        <div className="lg:hidden px-4 py-2 space-y-1 bg-[hsl(var(--background-tertiary))] border-b border-[hsl(var(--card-border)/0.3)]">
          <div className="flex justify-between text-[10px]">
            <span className="text-muted-foreground">Win Rate</span>
            <span className="font-mono-data">{(perf?.win_rate ?? 0).toFixed(1)}%</span>
          </div>
          <div className="flex justify-between text-[10px]">
            <span className="text-muted-foreground">Max DD</span>
            <span className="font-mono-data">-{(perf?.max_drawdown ?? 0).toFixed(1)}%</span>
          </div>
          <div className="flex justify-between text-[10px]">
            <span className="text-muted-foreground">Trades</span>
            <span className="font-mono-data">{perf?.total_trades ?? 0}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardHome;
